import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18n-react";
import * as FaIcons from "react-icons/fa";
import DefaultSpinner from "../../components/DefaultSpinner";
import DashboardPdfExportButton from "../../components/dashboard/DashboardPdfExportButton";
import { downloadPdf } from "../../components/dashboard/dashboardPdfUtils";
import { getDashboardThemeCssVariables, resolveDashboardTheme } from "../../components/dashboard/dashboardTheme";
import { getTodayDate, sanitizeOrdersDashboardFilters, useOrdersDashboard } from "../../hooks/useOrdersDashboard";
import AveragesPanel from "./components/AveragesPanel";
import ConversionPanel from "./components/ConversionPanel";
import OrdersDashboardFilters from "./components/OrdersDashboardFilters";
import RankingPanel, {
    CustomerRanking,
    ProductRanking,
    SellerRanking,
    useCustomerRankingModal,
    useProductRankingModal,
    useSellerRankingModal
} from "./components/RankingPanel";
import SummaryKpis from "./components/SummaryKpis";
import { formatPeriodDate } from "./components/ordersDashboardFormatters";
import { buildOrdersDashboardPdfDefinition } from "./ordersDashboardPdfDefinition";
import "../../components/dashboard/Dashboard.css";
import "./OrdersDashboard.css";

const getInstanceValue = (selector, fallback) => {
    return fallback || document.querySelector(selector)?.value || "";
};

const hasItems = (items) => Array.isArray(items) && items.length > 0;

const getBooleanValue = (selector) => {
    return String(document.querySelector(selector)?.value || "").toLowerCase() === "true";
};

const getVisibleUsersCount = (orgNodes) => {
    return (orgNodes.users || []).filter((user) => user.value).length;
};

const isDashboardEmpty = (dashboard) => {
    if (!dashboard) return false;

    return Number(dashboard.totalOrders || 0) === 0
        && Number(dashboard.totalSales || 0) === 0
        && Number(dashboard.uniqueCustomers || 0) === 0
        && Number(dashboard.executedVisits || 0) === 0
        && !hasItems(dashboard.sellers)
        && !hasItems(dashboard.products)
        && !hasItems(dashboard.productsByRevenue)
        && !hasItems(dashboard.customers);
};

const padDatePart = (value) => String(value).padStart(2, "0");

const formatPdfDownloadTimestamp = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = padDatePart(date.getMonth() + 1);
    const day = padDatePart(date.getDate());
    const hours = padDatePart(date.getHours());
    const minutes = padDatePart(date.getMinutes());
    return `${year}-${month}-${day} ${hours}-${minutes}`;
};

const OrdersDashboard = ({ token, independent, instanceType }) => {
    const [modalContent, setModalContent] = useState("");
    const currentInstanceType = getInstanceValue("#instanceType", instanceType);
    const currentIndependent = getInstanceValue("#independent", independent);
    const dashboardTheme = useMemo(() => resolveDashboardTheme({
        instanceType: currentInstanceType,
        independent: currentIndependent
    }), [currentIndependent, currentInstanceType]);
    const dashboardThemeVariables = useMemo(() => getDashboardThemeCssVariables(dashboardTheme), [dashboardTheme]);
    const {
        dashboard,
        filters,
        activeFilters,
        orgNodes,
        loading,
        loadingFilters,
        error,
        filterErrors,
        lastLoadedAt,
        updateFilter,
        updateType,
        applyPreset,
        setCustomDate,
        fetchDashboard,
        fetchDashboardRanking
    } = useOrdersDashboard({ setModalContent });
    const isAdmin = getBooleanValue("#isAdmin");
    const visibleUsersCount = getVisibleUsersCount(orgNodes);
    const visibilityMode = isAdmin || visibleUsersCount > 1 ? "org" : "self";
    const getRequestFilters = (currentFilters) => (
        visibilityMode === "self" ? sanitizeOrdersDashboardFilters(currentFilters) : currentFilters
    );

    useEffect(() => {
        if (token) {
            localStorage.setItem("auth_token", token);
        }
    }, [token]);

    const periodLabel = dashboard?.currentPeriod
        ? `${formatPeriodDate(dashboard.currentPeriod.dateStart)} - ${formatPeriodDate(dashboard.currentPeriod.dateEnd)}`
        : `${formatPeriodDate(filters.dateStart)} - ${formatPeriodDate(filters.dateEnd)}`;
    const previousPeriodLabel = dashboard?.previousPeriod
        ? `${formatPeriodDate(dashboard.previousPeriod.dateStart)} - ${formatPeriodDate(dashboard.previousPeriod.dateEnd)}`
        : "";

    const updatedAtLabel = String(i18n.translate("orders_dashboard_updated_at"));
    const lastLoadedTime = lastLoadedAt?.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
    const lastLoadedLabel = lastLoadedAt
        ? `${updatedAtLabel} ${lastLoadedTime}`
        : "";
    const sellerModal = useSellerRankingModal(dashboard?.sellers);
    const productModal = useProductRankingModal(dashboard?.products);
    const productRevenueModal = useProductRankingModal(dashboard?.productsByRevenue);
    const customerModal = useCustomerRankingModal(dashboard?.customers);
    const emptyDashboard = isDashboardEmpty(dashboard);
    const exportPdf = () => {
        const exportFilters = getRequestFilters(activeFilters);
        return Promise.all([
            fetchDashboardRanking("sellers", exportFilters),
            fetchDashboardRanking("products", exportFilters),
            fetchDashboardRanking("productsByRevenue", exportFilters),
            fetchDashboardRanking("customers", exportFilters)
        ]).then(([sellers, products, productsByRevenue, customers]) => {
            return buildOrdersDashboardPdfDefinition({
                dashboard,
                filters: exportFilters,
                orgNodes,
                instanceType: currentInstanceType,
                independent: currentIndependent,
                rankings: {
                    sellers,
                    products,
                    productsByRevenue,
                    customers
                }
            });
        }).then((docDefinition) => {
            downloadPdf(docDefinition, `Rendimiento Pedidos - ${formatPdfDownloadTimestamp()}.pdf`);
        });
    };

    return (
        <>
            {modalContent}
            {loading && (
                <DefaultSpinner
                    instanceType={currentInstanceType}
                    independent={currentIndependent}
                />
            )}
            <div className="orders-dashboard bodyContent" id="bodyContent" style={dashboardThemeVariables}>

                <OrdersDashboardFilters
                    filters={filters}
                    filterErrors={filterErrors}
                    maxDate={getTodayDate()}
                    orgNodes={orgNodes}
                    visibilityMode={visibilityMode}
                    loadingFilters={loadingFilters}
                    onApplyPreset={applyPreset}
                    onSetCustomDate={setCustomDate}
                    onUpdateType={updateType}
                    onUpdateFilter={updateFilter}
                    onSearch={() => fetchDashboard(getRequestFilters(filters))}
                />

                <div className="orders-dashboard-header">
                    <div>
                        <h1>{i18n.translate("orders_dashboard_title")}</h1>
                        <div className="orders-dashboard-subtitle">
                            <span>
                                <strong>{i18n.translate("orders_dashboard_current_period")}:</strong> {periodLabel}
                            </span>
                            {previousPeriodLabel && (
                                <span>
                                    <strong>{i18n.translate("orders_dashboard_previous_period")}:</strong> {previousPeriodLabel}
                                </span>
                            )}
                            {lastLoadedLabel && <span>{lastLoadedLabel}</span>}
                        </div>
                    </div>
                    <DashboardPdfExportButton
                        label={i18n.translate("orders_dashboard_export_pdf")}
                        loadingLabel={i18n.translate("default_loading")}
                        disabled={!dashboard}
                        onExport={exportPdf}
                    />
                </div>

                {error && <div className="alert alert-danger orders-dashboard-error">{i18n.translate("orders_dashboard_load_error")}: {error}</div>}

                {!error && emptyDashboard && (
                    <div className="orders-dashboard-empty">
                        <FaIcons.FaInfoCircle />
                        <span>{i18n.translate("orders_dashboard_no_data")}</span>
                    </div>
                )}

                {!error && dashboard && !emptyDashboard && (
                    <>
                        <SummaryKpis dashboard={dashboard} />
                        <AveragesPanel dashboard={dashboard} />

                        <div className="orders-divider" />
                        <div className="orders-section-label">{i18n.translate("orders_dashboard_detailed_performance")}</div>

                        <div className="orders-detail-grid">
                            <RankingPanel

                                title={i18n.translate("orders_dashboard_highlighted_customers")}
                                icon={<FaIcons.FaStar className="orders-title-icon amber" />}
                                modalItems={customerModal.items}
                                modalColumns={customerModal.columns}
                                modalSearchText={customerModal.getSearchText}
                                loadModalItems={(pagination) => fetchDashboardRanking("customers", getRequestFilters(activeFilters), pagination)}
                            >
                                <CustomerRanking customers={dashboard.customers} />

                            </RankingPanel>
                            <RankingPanel
                                title={i18n.translate("orders_dashboard_sellers_ranking")}
                                icon={<FaIcons.FaTrophy className="orders-title-icon amber" />}
                                modalItems={sellerModal.items}
                                modalColumns={sellerModal.columns}
                                modalSearchText={sellerModal.getSearchText}
                                loadModalItems={(pagination) => fetchDashboardRanking("sellers", getRequestFilters(activeFilters), pagination)}
                            >
                                <SellerRanking sellers={dashboard.sellers} />
                            </RankingPanel>

                        </div>

                        <div className="orders-detail-grid">

                            <RankingPanel
                                title={i18n.translate("orders_dashboard_top_revenue_products")}
                                icon={<FaIcons.FaChartLine className="orders-title-icon green" />}
                                modalItems={productRevenueModal.items}
                                modalColumns={productRevenueModal.columns}
                                modalSearchText={productRevenueModal.getSearchText}
                                loadModalItems={(pagination) => fetchDashboardRanking("productsByRevenue", getRequestFilters(activeFilters), pagination)}
                            >
                                <ProductRanking products={dashboard.productsByRevenue} />
                            </RankingPanel>
                 

                            <RankingPanel
                                title={i18n.translate("orders_dashboard_top_products")}
                                icon={<FaIcons.FaBoxOpen className="orders-title-icon blue" />}
                                modalItems={productModal.items}
                                modalColumns={productModal.columns}
                                modalSearchText={productModal.getSearchText}
                                loadModalItems={(pagination) => fetchDashboardRanking("products", getRequestFilters(activeFilters), pagination)}
                            >
                                <ProductRanking products={dashboard.products} />
                            </RankingPanel>

                            <ConversionPanel dashboard={dashboard} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

OrdersDashboard.propTypes = {
    token: PropTypes.string,
    independent: PropTypes.string,
    instanceType: PropTypes.string
};

OrdersDashboard.defaultProps = {
    token: "",
    independent: "",
    instanceType: ""
};

export default OrdersDashboard;
